import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Reproduces the label+control+error pattern repeated across every stage
 * form in this app (className="lbl" + input + a conditional red error line)
 * as one component instead of retyping that structure per field. Uses the
 * legacy `.lbl` class directly rather than a Tailwind reproduction of it —
 * same reasoning as reusing `.grid2`/`.card`: it already looks right, and a
 * parallel Tailwind version would be one more thing to keep in sync.
 */
export interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  /** Rendered inline after the label — e.g. a VoiceMicButton for this field. */
  labelExtra?: ReactNode;
  htmlFor?: string;
  /** Help text under the field — what the value is used for, or what can't change later. Hidden while an error shows. */
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function FormField({ label, required, error, labelExtra, htmlFor, hint, className, children }: FormFieldProps) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className={cn('lbl', labelExtra && 'flex items-center gap-1.5')}>
        <span>
          {label}
          {required && <span className="text-danger"> *</span>}
        </span>
        {labelExtra}
      </label>
      {children}
      {error && <div className="mt-[3px] text-[11px] text-danger">{error}</div>}
      {!error && hint && <div className="mt-1 text-[11.5px] leading-snug text-muted-foreground">{hint}</div>}
    </div>
  );
}
